/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React from "react";
import { find } from "lodash";
import { FieldZoneLayout } from "cms/fields/layout";
import { fieldTypes } from "data/resources/field-config";
import { isArray, objectHasProperty } from "utils/empty-exists";
import { Logger } from "cms/utils/log/logger";

export const getFieldConfiguration = (type) => {
    return find(fieldTypes, { type });
};

/**
 * This a factory method that returns a component based on the type.  It is
 * used in content-zone-edit where we are looping through content records each
 * with a given type.
 *
 * @param type
 * @returns {*|null}
 */
export const getFieldByType = (type) => {
    const fieldConfig = find(fieldTypes, { type });
    const component = fieldConfig?.component ?? null;
    if (!fieldConfig) {
        Logger.error(`Could not find configuration for field type: ${type}`);
    } else {
        // Returns a React Component
        return component;
    }
};

export const fieldsWithoutDefaults = ["field.component", "field.richtext"];

export const getIcon = (type) => {
    const fieldConfig = find(fieldTypes, { type });
    return fieldConfig?.icon;
};

export const getTitle = (type) => {
    const fieldConfig = find(fieldTypes, { type });
    const title = fieldConfig?.text ?? "";
    return title ;
};

export const getDefaultImpl = (type) => {
    const fieldConfig = find(fieldTypes, { type });
    const defaultImpl = fieldConfig?.defaultImpl;
    return structuredClone(defaultImpl);
};

export const getDefaultImplPopulated = (fieldType, fSchema) => {
    const fieldConfig = find(fieldTypes, { type: fieldType });
    const schemaSettings = fSchema?.settings;
    const defaultValue = schemaSettings?.defaultValue ?? null;
    const defaultStyle = schemaSettings?.defaultStyle ?? null;
    const defaultFormat = schemaSettings?.defaultFormat ?? null;
    // const noOverride = schemaSettings?.noOverride ?? null;
    const defaultImpl = structuredClone(fieldConfig?.defaultImpl);
    if (objectHasProperty(defaultImpl, "settings")) {
        defaultImpl.value = defaultValue ? defaultValue : "";
        defaultImpl.settings.style = defaultStyle ? defaultStyle : defaultImpl.settings.style;
        defaultImpl.settings.format = defaultFormat ? defaultFormat : defaultImpl.settings.format;
    }
    // Add an options object to the defaultImpl, if missing
    if (!objectHasProperty(defaultImpl?.settings, "options")) {
        defaultImpl.settings.options = {};
    }
    // For Documents: when options.target[] -> set options.openIn
    if (objectHasProperty(schemaSettings?.options, "targets") && isArray(schemaSettings?.options?.targets)) {
        try {
            defaultImpl.settings.options.openIn = schemaSettings?.options?.targets[0].value;
        } catch (err) {
            Logger.error(`failed to set default for target/openIn. ${err.message}`);
        }
    }
    return defaultImpl;
};

export const buildInitialState = (field) => {
    return structuredClone(field);
};

export const getFieldZoneLayout = (props) => {
    const displayComponent = props?.displayComponent;
    const editComponent = props?.editComponent;
    return (
        <FieldZoneLayout
            displayComponent={displayComponent}
            editComponent={editComponent}
        />
    );
};
